import { Injectable, WritableSignal, signal, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { buildQueryFilter } from '@shared/helpers/build-query-filter';
import { QueryOptions } from 'odata-query';
import {
  BehaviorSubject,
  Observable,
  combineLatest,
  map,
  shareReplay,
  switchMap,
} from 'rxjs';
import {
  EntityTypeResponseWithNestedObjectsODataWithMetadataResponse,
  GstarClient,
} from '../../../web-api-client';
import {
  mapEntityTypeFromResponse,
  mapEntityTypeToCreateRequest,
  mapEntityTypeToUpdateRequest,
} from '../mappers';
import { Collection, EntityType } from '../models';
import { ResidencyJurisdictionService } from './residency-jurisdiction.service';

@Injectable({
  providedIn: 'root',
})
export class EntityTypeService {
  private readonly gstarClient = inject(GstarClient);
  private readonly residencyJurisdictionService = inject(
    ResidencyJurisdictionService
  );

  private reloadEntityTypeListSubj: BehaviorSubject<void> =
    new BehaviorSubject<void>(undefined);

  private hasRefreshed = false;

  query: WritableSignal<Partial<QueryOptions<EntityType>>> = signal({
    filter: '',
  });

  entityTypesByJurisdiction: Observable<Collection<EntityType>> = combineLatest(
    [
      toObservable(this.residencyJurisdictionService.selectedJurisdiction),
      this.reloadEntityTypeListSubj,
    ]
  ).pipe(
    switchMap(() => {
      return this.gstarClient.entitytypesGET(
        undefined,
        buildQueryFilter(this.query()),
        undefined,
        undefined,
        undefined,
        undefined,
        undefined
      );
    }),
    map(
      (
        x:
          | EntityTypeResponseWithNestedObjectsODataWithMetadataResponse
          | undefined
      ) => {
        return {
          total: x?.odataCount ?? 0,
          items:
            x?.value
              ?.map(responseEntityType =>
                mapEntityTypeFromResponse(responseEntityType)
              )
              .filter(
                type =>
                  type.country?.countryId ===
                    this.residencyJurisdictionService.selectedJurisdiction()
                      ?.country?.countryId || !type.country
              ) ?? [],
        };
      }
    ),
    shareReplay(1)
  );

  get(
    query?: Partial<QueryOptions<EntityType>>
  ): Observable<Collection<EntityType>> {
    return this.gstarClient
      .entitytypesGET(
        undefined,
        buildQueryFilter(query),
        query?.top?.toString(),
        query?.skip?.toString(),
        query?.orderBy?.toString(),
        query?.count?.toString(),
        undefined
      )
      .pipe(
        map(response => {
          return {
            total: response.odataCount ?? 0,
            items:
              response.value?.map(responseEntity =>
                mapEntityTypeFromResponse(responseEntity)
              ) ?? [],
          };
        })
      );
  }

  getById(entityTypeId: number): Observable<EntityType> {
    return this.gstarClient
      .entitytypesGET2(entityTypeId)
      .pipe(map(response => mapEntityTypeFromResponse(response)));
  }

  create(entityType: EntityType): Observable<EntityType> {
    return this.gstarClient
      .entitytypesPOST(mapEntityTypeToCreateRequest(entityType))
      .pipe(map(response => mapEntityTypeFromResponse(response)));
  }

  update(entityType: EntityType): Observable<void> {
    return this.gstarClient.entitytypesPUT(
      entityType.entityTypeId,
      mapEntityTypeToUpdateRequest(entityType)
    );
  }

  cancelRefreshEntityTypes(): void {
    this.hasRefreshed = false;
  }

  refreshEntityTypes(): void {
    if (!this.hasRefreshed) {
      this.reloadEntityTypeListSubj.next();
      this.hasRefreshed = true;
    }
  }
}
